<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useField, useForm } from 'vee-validate'
import { useI18n } from 'vue-i18n'
import { object, string, array } from 'yup'
import { getWorkspace } from '@/app/core/services/roles'
import {
  DBtn,
  DTab,
  DTabs,
  DLoading,
  DTextField,
  DSelectFieldSearch,
  DMultiselect,
  DModal
} from 'front-library'
import {
  createUser,
  updateUser,
  getUser,
  getPayrolls
} from '@/app/core/services/users'
usePayrollChange(() =>
  Boolean(route.params.id) ? back() : window.location.reload()
)

/* store, router, notifications */
const router = useRouter()
const route = useRoute()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Datos formulario */
const dataUser = ref({
  id: '',
  name: '',
  email: '',
  defaultRole: '',
  allowedRoles: undefined,
  allowedPayrolls: undefined
})
const viewUser = ref(route.name === 'GetUser')

/* Datos inactivos (son campos que dependen de la seleccion de otros campos) */
const disabledDefaultRole = ref(true)
const disabledEmail = ref(false)

/* Datos Listas */
const itemsDefaultRol = ref([])
const searchDefaultRol = ref('')
const itemsRoles = ref([] as any)
const itemsPayrolls = ref([] as any)

/* Datos componente Modal y Loading */
const showModal = ref(false)

const loading = ref(false)
const messageLoading = ref('')

/* Esquema de validacion campos obligatorios */
const validationSchema = object().shape({
  email: string()
    .required(t('form.valid.required'))
    .matches(
      /^[a-zA-Z0-9.-_]{0,50}@[a-zA-Z0-9]{0,50}\.[a-zA-Z]{2,10}$/g,
      t('form.valid.email')
    ),
  name: string()
    .required(t('form.valid.required'))
    .max(80, t('form.valid.maxLength', [80]))
    .matches(
      /^[A-Za-z0-9À-ÿ\u00f1\u00d1\s-_&()]+$/,
      t('form.valid.alphanumeric')
    ),
  defaultRole: string().required(t('form.valid.required')),
  allowedRoles: array().required(t('form.valid.required')),
  allowedPayrolls: array().required(t('form.valid.required'))
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm({
  initialValues: dataUser.value,
  validationSchema
})

/* Definicion de  campos del formulario */
const { value: name } = useField<string>('name')
const { value: email } = useField<string>('email')
const { value: defaultRole } = useField<string>('defaultRole')
const { value: allowedRoles } = useField<string[]>('allowedRoles')
const { value: allowedPayrolls } = useField<string[]>('allowedPayrolls')

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values, actions) => {
  void (route.params.id === undefined ? create(values) : update(values))
})

/* Metodo Guardar usuarios */
const create = async (values: any) => {
  loading.value = true
  await createUser(values)
    .then(response => {
      loading.value = false
      notifications.addNotification({
        title: t('notification.created'),
        text: t('users.notification.action', [t('notification.wasCreated')]),
        type: 'success',
        time: 10000
      })
      void router.push({ name: 'ListUsers' })
    })
    .catch((error: any) => {
      notificationError(error, 'users', t, notifications, loading)
    })
}

/* Metodo Editar usuarios */
const update = async (values: any) => {
  loading.value = true
  await updateUser(dataUser.value.id.toString(), values)
    .then(response => {
      loading.value = false
      notifications.addNotification({
        title: t('notification.edited'),
        text: t('users.notification.edit'),
        type: 'success',
        time: 12000
      })
      void router.push({ name: 'ListUsers' })
    })
    .catch((error: any) => {
      notificationError(error, 'users', t, notifications, loading)
    })
}

/* Habilitar lista de rol por defecto */
const enabledRoleDefault = (idsRole: any) => {
  if (idsRole?.length > 0) {
    const roles: any = []
    idsRole.forEach((element: any) => {
      roles.push(
        itemsRoles.value.assignedRoles?.filter((_: any) => _.id === element)[0]
      )
    })
    itemsDefaultRol.value = roles
    if (idsRole.length === 1) defaultRole.value = roles[0].id
    disabledDefaultRole.value = false
  } else {
    defaultRole.value = ''
    disabledDefaultRole.value = true
  }
}

/* Volver */
const back = () => {
  if (
    (name.value === '' &&
      email.value === '' &&
      defaultRole.value === '' &&
      ((allowedRoles.value as any)?.length === 0 ||
        allowedRoles.value === undefined) &&
      ((allowedPayrolls.value as any)?.length === 0 ||
        allowedPayrolls.value === undefined)) ||
    route.params.id !== undefined
  ) {
    void router.push({ name: 'ListUsers' })
  } else {
    showModal.value = true
  }
}
const backConfirm = () => {
  void router.push({ name: 'ListUsers' })
}

/* Metodo para cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
}

onMounted(async () => {
  loading.value = true
  try {
    itemsRoles.value = await getWorkspace(store.user?.workspaceId)
    const payrolls = await getPayrolls()
    itemsPayrolls.value = payrolls.results
    if (route.params.id !== undefined) {
      messageLoading.value = t('loading.data')
      const data = await getUser(route.params.id.toString())
      dataUser.value.id = data.id
      dataUser.value.name = data.name
      dataUser.value.email = data.email
      const roles: any = []
      data.allowedRoles.forEach((element: any) => {
        roles.push(element.id)
      })
      dataUser.value.allowedRoles = roles
      const payrolls: any = []
      data.allowedPayrolls.forEach((element: any) => {
        payrolls.push(element.id)
      })
      dataUser.value.allowedPayrolls = payrolls
      dataUser.value.defaultRole = data.defaultRole?.id
      enabledRoleDefault(roles)
      disabledEmail.value = true
    }
    loading.value = false
  } catch (error: any) {
    if (error.response.status === 400) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('users.notification.error'),
        type: 'error',
        time: 10000
      })
      back()
    } else {
      notificationError(error, 'users', t, notifications, loading)
    }
  }
})
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    typeAlert="question"
    :title="t('modal.confirmationQuestion')"
    :description="t('modal.pendingChangesMessage')"
    :acceptQuestion="
      t('modal.acceptQuestion', { action: t('table.action.leave') })
    "
    @closeModal="closeModal"
    @otherMethod="backConfirm()" />
  <div class="h-full px-5">
    <div class="bg-gray-50 pb-2 sm:pb-4">
      <div class="leading-4 pt-responsive">
        <p class="text-3xl font-semibold text-shadow text-blue-900">
          {{
            route.params.id == undefined
              ? t('users.create')
              : viewUser
              ? t('users.get')
              : t('users.edit')
          }}
        </p>
      </div>
    </div>
    <form class="pt-responsive" @submit.prevent="onSubmit">
      <DTabs>
        <template v-slot:default="slotParams">
          <DTab
            :title="t('tabs.data')"
            :selected-title="slotParams?.selectedTitle">
            <div class="grid grid-cols-8 px-6 py-4">
              <div
                class="col-span-8 sm:col-span-3 lg:col-span-2 text-base font-semibold text-gray-700 pb-2 sm:pb-0">
                {{ t('groups.basics.title') }}
              </div>
              <div
                class="col-span-8 sm:col-span-5 lg:col-span-6 grid grid-cols-6 gap-x-6 gap-y-4">
                <DTextField
                  class="col-span-8 sm:col-span-6 lg:col-span-3 xl:col-span-2"
                  :label="t('users.fields.email.label')"
                  type="email"
                  :disabled="disabledEmail || viewUser"
                  :error="errors.email"
                  :placeholder="t('users.fields.email.placeholder')"
                  :required="!disabledEmail"
                  v-model="email"
                  icon="Mail01Icon" />
                <DTextField
                  class="col-span-8 sm:col-span-6 lg:col-span-3 xl:col-span-2"
                  :label="t('users.fields.name.label')"
                  :error="errors.name"
                  :placeholder="t('users.fields.name.placeholder')"
                  :required="true"
                  :disabled="viewUser"
                  v-model="name"
                  icon="User01Icon" />
                <DMultiselect
                  class="col-span-8 sm:col-span-6 lg:col-span-3 xl:col-span-2"
                  :label="t('users.fields.roles.label')"
                  @change="enabledRoleDefault(allowedRoles)"
                  :error="errors.allowedRoles"
                  :required="true"
                  :disabled="viewUser"
                  v-model="allowedRoles"
                  :options="itemsRoles.assignedRoles"
                  value-key="id"
                  :header-title="t('users.fields.roles.label')" />
                <DSelectFieldSearch
                  class="col-span-8 sm:col-span-6 lg:col-span-3 xl:col-span-2"
                  :label="t('users.fields.rol.label')"
                  :options="filterOptions(itemsDefaultRol, searchDefaultRol)"
                  @filter="searchDefaultRol = $event"
                  value-key="id"
                  :error="errors.defaultRole"
                  :required="true"
                  :disabled="disabledDefaultRole || viewUser"
                  v-model="defaultRole" />
                <DMultiselect
                  class="col-span-8 sm:col-span-6 lg:col-span-3 xl:col-span-2"
                  :label="t('users.fields.payroll.label')"
                  :error="errors.allowedPayrolls"
                  :required="true"
                  :disabled="viewUser"
                  v-model="allowedPayrolls"
                  :options="itemsPayrolls"
                  value-key="id"
                  :header-title="t('users.fields.payroll.label')" />
              </div>
            </div>
            <div
              class="px-6 py-4 flex justify-end"
              v-if="route.params.id !== undefined">
              <p
                class="text-sm font-semibold text-blue-900"
                v-html="t('users.notification.info')" />
            </div>
          </DTab>
        </template>
      </DTabs>
      <div class="p-5 bg-white shadow-md h-24 rounded-br-md rounded-bl-md">
        <hr class="pb-5" />
        <div class="flex justify-end gap-2 items-center">
          <DBtn
            type="button"
            class="font-semibold py-1 text-base"
            color="secondary"
            @click="back()">
            {{ viewUser ? t('form.button.back') : t('form.button.cancel') }}
          </DBtn>
          <DBtn
            v-if="!viewUser"
            class="font-semibold py-1 text-base"
            color="blue">
            {{
              route.params.id === undefined
                ? t('form.button.save')
                : t('form.button.update')
            }}
          </DBtn>
        </div>
      </div>
    </form>
  </div>
</template>
